import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useUrlFilters } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import { pluraliseUnit } from "/imports/libs/format";
import { DataView, EmptyState, FilterDropdown, PurchaseConfirmDialog } from "/imports/ui/components/shared";
import { iconTileColors } from "/imports/ui/components/discovery";

function formatVoucherValidity(opt) {
  if (opt.noExpiry) return "No expiry";
  if (opt.duration && opt.durationUnit) return `Valid for ${opt.duration} ${pluraliseUnit(opt.duration, opt.durationUnit)}`;
  return null;
}

function VoucherRow({ voucher, opt, currency, onBuy, isBuying }) {
  const price = Number.isFinite(opt.price) ? opt.price : parseFloat(opt.price) || 0;
  const value = Number.isFinite(voucher.value) ? voucher.value : parseFloat(voucher.value) || 0;
  const validity = formatVoucherValidity(opt);
  // A voucher worth more than its price hands the client bonus credit.
  const hasBonus = value > price;

  return (
    <Card variant="outlined">
      <CardContent
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 1.5, sm: 2 },
          py: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 },
          "&:last-child": { pb: { xs: 2, sm: 2.5 } }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <Box
            sx={[
              {
                width: "2.5rem",
                height: "2.5rem",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              },
              (t) => iconTileColors(t, 0.08)
            ]}
          >
            <CardGiftcardOutlinedIcon sx={{ fontSize: "1.375rem" }} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: { xs: "-webkit-box", sm: "block" },
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: { xs: 2, sm: "unset" },
                whiteSpace: { xs: "normal", sm: "nowrap" }
              }}
            >
              {voucher.name}
            </Typography>
            {hasBonus && (
              <Chip
                size="small"
                label={`Bonus ${currency} ${(value - price).toFixed(2)}`}
                variant="outlined"
                sx={{ height: "1.25rem", fontSize: "0.65rem", color: "primary.main", borderColor: "primary.main" }}
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary">
            {currency} {value.toFixed(2)} credit
            {validity ? ` · ${validity}` : ""}
          </Typography>
          {voucher.description && (
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 0.5 }}>
              {voucher.description}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
            ml: { sm: "auto" },
            flexBasis: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
            mt: { xs: 0.5, sm: 0 },
            pt: { xs: 1.5, sm: 0 },
            borderTop: { xs: "1px solid", sm: "none" },
            borderColor: "divider"
          }}
        >
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: { xs: "1.0625rem", sm: "1.25rem" } }}>
              {currency} {price.toFixed(2)}
            </Typography>
            {hasBonus && (
              <Typography variant="caption" color="primary.main" sx={{ fontSize: { xs: "0.6875rem", sm: "0.75rem" } }}>
                Get {currency} {value.toFixed(2)}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            disabled={isBuying}
            onClick={() => onBuy(voucher, opt)}
            sx={{ textTransform: "none", color: "common.white", minWidth: "5.625rem" }}
          >
            {isBuying ? <CircularProgress size={18} /> : "Buy"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function VouchersView() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency, locations } = useWidget();
  const { isAuthenticated } = useAuth();
  const { filters, setFilter } = useUrlFilters({});

  const { data, loading, error } = useFetch(() => api.listVouchersForSale(slug), [slug], { skip: !slug });
  const vouchers = data?.items || [];

  const [buyingKey, setBuyingKey] = useState(null);
  // Pending purchase awaiting confirmation: { name, lines, price, run }.
  const [confirm, setConfirm] = useState(null);

  // Some vouchers are sold at only certain branches — when the widget spans
  // multiple locations the client can narrow the catalogue to one.
  const visibleVouchers = filters.locationId ? vouchers.filter((v) => (v.locationIds || []).includes(filters.locationId)) : vouchers;

  const voucherRows = visibleVouchers.flatMap((voucher) =>
    (voucher.pricingOptions || []).map((opt) => ({
      voucher,
      opt,
      key: `v:${voucher._id}:${opt.pricingId}`
    }))
  );

  const doPurchase = async (voucher, opt) => {
    setBuyingKey(`v:${voucher._id}:${opt.pricingId}`);
    try {
      const result = await api.purchaseVoucher(slug, {
        voucherId: voucher._id,
        pricingId: opt.pricingId
      });
      if (result.chargeId) {
        sessionStorage.setItem("widget_slug", slug);
        navigate(`/checkout/telr/${result.chargeId}`);
        return;
      }
      enqueueSnackbar("Purchase complete", { variant: "success" });
      navigate(`/${slug}/dashboard`);
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to start purchase", { variant: "error" });
    } finally {
      setBuyingKey(null);
    }
  };

  const handleBuy = (voucher, opt) => {
    if (!isAuthenticated) {
      navigate(`/${slug}/login?redirect=/${slug}/vouchers`);
      return;
    }
    const value = parseFloat(voucher.value) || 0;
    const validity = formatVoucherValidity(opt);
    setConfirm({
      name: voucher.name,
      lines: [`${(currency || "").trim()} ${value.toFixed(2)} credit`.trim(), validity].filter(Boolean),
      price: parseFloat(opt.price) || 0,
      run: () => doPurchase(voucher, opt)
    });
  };

  const showLocationFilter = (locations || []).length > 1;

  return (
    <Box>
      <Box mb={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <IconButton size="small" onClick={() => navigate(`/${slug}`)} aria-label="Back to home" sx={{ ml: -0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            Vouchers
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          Buy a gift voucher to redeem against bookings and packages later.
        </Typography>
      </Box>

      {showLocationFilter && (
        <Box mb={3} sx={{ maxWidth: "17.5rem" }}>
          <FilterDropdown
            label="Location"
            options={(locations || []).map((l) => ({ value: l._id, label: l.name }))}
            value={filters.locationId}
            onChange={(val) => setFilter("locationId", val)}
            allLabel="All Locations"
            fullWidth
          />
        </Box>
      )}

      <DataView loading={loading} error={error} data={[1]} loadingMessage="Loading vouchers...">
        {voucherRows.length === 0 ? (
          <EmptyState
            title="No vouchers available"
            description={
              filters.locationId
                ? "No vouchers are sold at this location — try another one."
                : "Check back later — this club hasn't published any vouchers yet."
            }
          />
        ) : (
          <Stack spacing={2}>
            {voucherRows.map(({ voucher, opt, key }) => (
              <VoucherRow key={key} voucher={voucher} opt={opt} currency={currency || ""} onBuy={handleBuy} isBuying={buyingKey === key} />
            ))}
          </Stack>
        )}
      </DataView>

      <PurchaseConfirmDialog
        open={!!confirm}
        name={confirm?.name}
        lines={confirm?.lines || []}
        price={confirm?.price}
        currency={currency || ""}
        loading={buyingKey != null}
        onConfirm={async () => {
          await confirm?.run();
          setConfirm(null);
        }}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
}

export default VouchersView;
